import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { demandAPI, offerAPI } from '../../services/api';
import { OrderDetailsSkeleton, EmptyState } from '../../components/skeletons';
import { AttributeValues } from '../../components/AttributeFields';

export default function DemandDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [demand, setDemand] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    Promise.all([
      demandAPI.get(id),
      offerAPI.byDemand(id),
    ])
      .then(([dRes, oRes]) => {
        setDemand(dRes.data.data || dRes.data);
        setOffers(oRes.data.data || oRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const handleAccept = async (offerId) => {
    setActionLoading(offerId);
    try {
      await offerAPI.accept(offerId);
      setOffers(prev => prev.map(o => o._id === offerId ? { ...o, status: 'accepted' } : o.status === 'pending' ? { ...o, status: 'rejected' } : o));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to accept offer');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (offerId) => {
    setActionLoading(offerId);
    try {
      await offerAPI.reject(offerId);
      setOffers(prev => prev.map(o => o._id === offerId ? { ...o, status: 'rejected' } : o));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reject offer');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCloseDemand = async () => {
    if (!window.confirm('Are you sure you want to close this demand? No more offers will be accepted.')) return;
    try {
      await demandAPI.close(id);
      setDemand(prev => ({ ...prev, status: 'closed' }));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to close demand');
    }
  };

  if (loading) {
    return <OrderDetailsSkeleton />;
  }

  if (!demand) {
    return (
      <div style={{ padding: 24, maxWidth: 600, margin: '40px auto' }}>
        <EmptyState
          title="Demand not found"
          description="This demand requirement may have been deleted or is no longer accessible."
          actionLabel="Back to My Demands"
          onAction={() => navigate('/buyer/demands')}
        />
      </div>
    );
  }

  const statusColor = s => {
    if (s === 'active') return 'tag-green';
    if (s === 'closed') return 'tag-blue';
    if (s === 'accepted') return 'tag-green';
    if (s === 'rejected') return 'tag-red';
    return 'tag-gold';
  };

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      {/* Back */}
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 16 }} onClick={() => navigate('/buyer/demands')}>
        &#8592; Back to My Demands
      </button>

      {/* Demand Info Card */}
      <div className="card" style={{ padding: '24px 28px', marginBottom: 24, borderLeft: '4px solid var(--green)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span className="heading-md">{demand.title}</span>
              <span className={`tag ${statusColor(demand.status)}`}>{demand.status?.toUpperCase()}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {demand.categoryId?.name && <span className="tag tag-green">{demand.categoryId.name}</span>}
              {demand.subCategoryId?.name && <span className="tag" style={{ background: 'var(--gold-lt)', color: 'var(--gold-dk)' }}>{demand.subCategoryId.name}</span>}
              {demand.brandId?.name && <span className="tag" style={{ background: 'var(--gray-lt)', color: 'var(--muted)' }}>{demand.brandId.name}</span>}
              {demand.nationwide && <span className="tag tag-blue">NATIONWIDE</span>}
              {demand.wantsLoan && <span className="tag" style={{ background: '#FFF4D6', color: '#8A5A00' }}>🏦 FINANCING</span>}
            </div>
          </div>
          {demand.status === 'active' && (
            <button className="btn btn-red btn-sm" onClick={handleCloseDemand}>Close Demand</button>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
          <div>
            <div className="text-muted text-xs">Your Price</div>
            <div style={{ fontFamily: 'var(--font-serif)', fontWeight: 700, fontSize: 18, color: 'var(--green)', marginTop: 4 }}>
              {demand.price != null ? `\u20B9${Number(demand.price).toLocaleString('en-IN')}` : '--'}
            </div>
          </div>
          <div>
            <div className="text-muted text-xs">Quantity</div>
            <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>{demand.qty || 1} unit{(demand.qty || 1) > 1 ? 's' : ''}</div>
          </div>
          <div>
            <div className="text-muted text-xs">Location</div>
            <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>
              {demand.nationwide
                ? 'Nationwide'
                : [demand.location?.city, demand.location?.state].filter(Boolean).join(', ') || '--'}
            </div>
          </div>
          <div>
            <div className="text-muted text-xs">Posted On</div>
            <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>
              {new Date(demand.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
          </div>
        </div>

        {/* Financing — only shown when the buyer flagged this as a loan lead */}
        {demand.wantsLoan && (
          <div style={{ background: '#FFF8E7', border: '1px solid #EAC475', borderRadius: 10, padding: '12px 16px', marginBottom: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <div className="text-muted text-xs">Loan requested</div>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4, color: '#8A5A00' }}>Yes</div>
            </div>
            <div>
              <div className="text-muted text-xs">Minimum down payment</div>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 4, color: '#8A5A00' }}>
                {demand.downPayment != null ? `\u20B9${Number(demand.downPayment).toLocaleString('en-IN')}` : '--'}
              </div>
            </div>
            <div style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--muted)' }}>
              Routed to banks / NBFCs only. Regular vendors don't see this lead.
            </div>
          </div>
        )}

        {Array.isArray(demand.attributes) && demand.attributes.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 7 }}>Specifications</div>
            <AttributeValues items={demand.attributes} />
          </div>
        )}

        {demand.description && (
          <div style={{ background: 'var(--cream)', borderRadius: 'var(--radius)', padding: '12px 16px', fontSize: 13, lineHeight: 1.6, color: 'var(--char)' }}>
            {demand.description}
          </div>
        )}
      </div>

      {/* Offers Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div className="heading-sm">Vendor Offers ({offers.length})</div>
      </div>

      {offers.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <div className="heading-sm" style={{ marginBottom: 6 }}>No offers yet</div>
          <p className="text-muted text-sm">Vendors are reviewing your demand. Offers will appear here.</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--cream)', borderBottom: '1.5px solid var(--border)' }}>
                  {['Vendor', 'Price', 'Delivery', 'Warranty', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {offers.map(o => {
                  // The offers endpoint attaches `vendorProfile` with a
                  // kycVerified flag — preferred over the deprecated
                  // `o.vendor.verified` hint.
                  const vp = o.vendorProfile || {};
                  const vendorName = vp.firmName || o.vendor?.businessName || o.vendorId?.fullName || o.vendor?.fullName || 'Vendor';
                  const kycVerified = !!vp.kycVerified;
                  return (
                  <tr key={o._id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%', background: 'var(--green-lt)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, fontWeight: 700, color: 'var(--green)',
                        }}>
                          {(vendorName).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: 'var(--char)', display: 'flex', alignItems: 'center', gap: 6 }}>
                            {vendorName}
                            {kycVerified && (
                              <span title="KYC verified by Kanjusee" style={{ background: '#E8F5EE', color: '#1B5C3A', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, letterSpacing: 0.3 }}>
                                ✓ Verified
                              </span>
                            )}
                          </div>
                          {vp.city && <div className="text-muted text-xs">📍 {vp.city}{vp.state ? `, ${vp.state}` : ''}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', fontFamily: 'var(--font-serif)', fontWeight: 700, color: 'var(--green)' }}>
                      {o.price ? `\u20B9${o.price.toLocaleString('en-IN')}` : '--'}
                    </td>
                    <td style={{ padding: '14px 16px', color: 'var(--char)' }}>
                      {o.deliveryTime || (o.deliveryDays ? `${o.deliveryDays} days` : '--')}
                    </td>
                    <td style={{ padding: '14px 16px', color: 'var(--char)' }}>
                      {o.warranty || '--'}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span className={`tag ${statusColor(o.status)}`}>{o.status?.toUpperCase() || 'PENDING'}</span>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        {o.status === 'pending' && demand.status === 'active' && (
                          <>
                            <button
                              className="btn btn-pri btn-sm"
                              onClick={() => handleAccept(o._id)}
                              disabled={actionLoading === o._id}
                            >
                              Accept
                            </button>
                            <button
                              className="btn btn-out btn-sm"
                              style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                              onClick={() => handleReject(o._id)}
                              disabled={actionLoading === o._id}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        <button
                          className="btn btn-pri btn-sm"
                          style={{ background: '#1B5C3A', borderColor: '#1B5C3A' }}
                          onClick={() => navigate(`/buyer/messages?offerId=${o._id}`)}
                        >
                          💬 Chat
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
